/**
 * Event Type Categories
 * Maps event categories to their individual event types
 * Based on the 24 event types displayed on the /icons page
 */

export const EVENT_TYPE_CATEGORIES = {
  pricing: [
    'price_increased',
    'price_decreased',
    'price_hidden',
    'price_revealed',
    'discount_added',
    'discount_removed',
    'discount_changed',
    'pricing_restructured',
  ],
  packaging: [
    'plan_added',
    'plan_removed',
    'plan_renamed',
    'pricing_metric_changed',
    'threshold_added',
    'threshold_removed',
    'capacity_increased',
    'capacity_decreased',
    'trial_changed',
    'freemium_changed',
    'addon_added',
    'addon_removed',
    'addon_changed',
  ],
  product: [
    'feature_added',
    'feature_removed',
    'feature_changed',
    'positioning_changed',
  ],
} as const;

export type EventCategory = keyof typeof EVENT_TYPE_CATEGORIES;

export type EventType = typeof EVENT_TYPE_CATEGORIES[EventCategory][number];

/**
 * Get all event types for given categories
 */
export function getEventTypesForCategories(categories: string[]): string[] {
  const types = new Set<string>();
  categories.forEach(cat => {
    const categoryTypes = EVENT_TYPE_CATEGORIES[cat as EventCategory];
    if (categoryTypes) {
      categoryTypes.forEach(type => types.add(type));
    }
  });
  return Array.from(types);
}

/**
 * Get category for an event type
 */
export function getCategoryForEventType(eventType: string): EventCategory | undefined {
  for (const [category, types] of Object.entries(EVENT_TYPE_CATEGORIES)) {
    if (types.includes(eventType as any)) {
      return category as EventCategory;
    }
  }
  return undefined;
}

/**
 * Check if all types in a category are selected
 */
export function isCategoryFullySelected(category: EventCategory, selectedTypes: Set<string>): boolean {
  const categoryTypes = EVENT_TYPE_CATEGORIES[category];
  return categoryTypes.every(type => selectedTypes.has(type));
}

/**
 * Category metadata for UI display
 */
export const CATEGORY_METADATA = {
  pricing: {
    label: 'Pricing Events',
    count: EVENT_TYPE_CATEGORIES.pricing.length,
    description: 'Price changes, discounts, and pricing structure updates',
  },
  packaging: {
    label: 'Packaging Events',
    count: EVENT_TYPE_CATEGORIES.packaging.length,
    description: 'Plan changes, limits, trials, and add-ons',
  },
  product: {
    label: 'Product Events',
    count: EVENT_TYPE_CATEGORIES.product.length,
    description: 'Feature additions, removals, and changes',
  },
} as const;

/**
 * Get all event types (flat array)
 */
export function getAllEventTypes(): string[] {
  return [
    ...EVENT_TYPE_CATEGORIES.pricing,
    ...EVENT_TYPE_CATEGORIES.packaging,
    ...EVENT_TYPE_CATEGORIES.product,
  ];
}
